/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @description Component-TradingPairsDrawer
 */
import './TradingPairsDrawer.less';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import NoSearchResult from '@/assets/svg/search_empty.svg?react';
import Drawer from '@/components/Drawer';
import { TableCard } from '@/components/TableCard';
import TableSearchBar from '@/components/TableSearchBar';
import PairSearch from '@/components/TableSearchBar/PairSearch';
import { useChainId } from '@/hooks/web3/useChain';

import TradeYourOwnRiskAlert from '@/components/Alert/TradeYourOwnRiskAlert';
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import { useMockDevTool } from '@/components/Mock';
import TokenFavorite from '@/components/TokenFavorite';
import TokenLogo from '@/components/TokenLogo';
import { TokenPairProps } from '@/components/TokenPair';
import TokenPairLeverage from '@/components/TokenPair/TokenPairLeverage';
import TokenPairOracle from '@/components/TokenPair/TokenPairOracle';
import TokenPairSymbol from '@/components/TokenPair/TokenPairSymbol';
import { PAIR_PAGE_TYPE } from '@/constants/global';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useBackendChainConfig } from '@/features/config/hook';
import {
  useFilteredTradingPairs,
  useMarginSearch,
  useMarketList,
  useWatchMarketListChange,
} from '@/features/market/hooks';
import { IMarketPair } from '@/features/market/types';
import { useSideNavigate } from '@/hooks/useRouterNavigate';
import { marketColumnRowKey } from '@/pages/Market/TradingPairs/tableTitles';
import Change24 from '@/pages/components/Change24';
import { CUSTOM_SELECTOR } from '@/types/global';
import { bigNumberSort } from '@/utils/numberUtil';
import { ColumnsType } from 'antd/es/table';
import classNames from 'classnames';
import _ from 'lodash';

interface IPropTypes {
  onClose: () => void;
  showPopup: boolean;
}

function TradingTokenPair({
  baseToken,
  quoteToken,
  expiry,
  showFavorite,
  instrumentAddr,
  infoMode,
  tokenSize,
  isInverse = false,
  chainIconSize = 16,
  showOracle = false,
  showExpiry = false,
  marketType,
  highlightExpiry,
  isShowLogo = true,
  isShowExpiryTag = false,
  showShortPerp = true,
  leverage,
  showLeverage,
  extra,
  verticalMode,
  tagExtra,
  dropdownIcon,
  isMobile,
  showMargin,
  chainId,
  // symbol,
  showChainIcon,
  showMarginText,
  requiredMarginIcon,
}: TokenPairProps) {
  const { t } = useTranslation();
  return (
    <div className="syn-token-pair-vertical">
      <>{showFavorite && <TokenFavorite chainId={chainId} instrumentAddr={instrumentAddr} expiry={expiry} />}</>
      <div className={classNames('token-pair', 'compact', 'vertical', isMobile && 'mobile')}>
        <div className="token-pair-content">
          <div className="token-pair-content-top">
            {isShowLogo && (
              <TokenLogo
                chainId={chainId}
                showChainIcon={showChainIcon}
                size={tokenSize || 24}
                token={isInverse ? quoteToken : baseToken}
              />
            )}
            <TokenPairSymbol
              requiredMarginIcon={false}
              isInverse={isInverse}
              baseToken={baseToken}
              quoteToken={quoteToken}
            />
            {dropdownIcon}
          </div>
          <div className="token-pair-content-bottom">
            {showMarginText && (
              <div className="token-pair-content-bottom-margin">
                {quoteToken.symbol} {t('common.margin')}
              </div>
            )}

            {showLeverage && <TokenPairLeverage leverage={leverage} highlight={highlightExpiry} />}
            {showOracle && <TokenPairOracle marketType={marketType} />}
            {tagExtra}
          </div>
        </div>
      </div>
    </div>
  );
}

const TradingPairsDrawer: FC<IPropTypes> = ({ onClose, showPopup }) => {
  const chainId = useChainId();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sorter, setSorter] = useState<any>();
  const chainConfig = useBackendChainConfig(chainId);
  const { pairPageNavigate } = useSideNavigate();
  const {
    marginSearchProps: { marginCustomSelectorId, search, marginSelector },
  } = useMarginSearch();
  const { marketPairList, isFetched } = useMarketList(chainId);
  const [scrollY, setScrollY] = useState(251);

  const { filteredPairs } = useFilteredTradingPairs();
  const columnDefs = useMemo(() => {
    const columns: ColumnsType<IMarketPair> = [
      {
        title: t('common.pair'),
        dataIndex: 'pair',
        width: 140,
        align: 'left',
        shouldCellUpdate: (record: IMarketPair, prevRecord: IMarketPair): boolean => false,
        render: (text: string, record: IMarketPair): JSX.Element | undefined => {
          return (
            <TradingTokenPair
              verticalMode={true}
              showFavorite={true}
              requiredMarginIcon={false}
              tokenSize={16}
              baseToken={record?.baseToken}
              quoteToken={record?.quoteToken}
              expiry={record?.expiry}
              // verticalMode={true}
              showLeverage={true}
              instrumentAddr={record.instrumentAddr}
              chainId={record?.chainId}
              isShowExpiryTag
              showShortPerp={true}
              leverage={record.maxLeverage}
              showMarginText={false}
            />
          );
        },
      },
      {
        title: t('common.table.24hv'),
        dataIndex: '24hV',
        width: 60,
        showSorterTooltip: false,
        align: 'left',
        defaultSortOrder: 'descend',
        shouldCellUpdate: (record: IMarketPair, prevRecord: IMarketPair): boolean => true,
        sorter: (a: IMarketPair, b: IMarketPair): number => {
          return bigNumberSort(
            WrappedBigNumber.from(a?.volume24hUsd || 0),
            WrappedBigNumber.from(b?.volume24hUsd || 0),
          );
        },
        render: (_, record): JSX.Element | undefined => {
          return (
            <EmptyDataWrap isLoading={!record?.baseVolume24h}>
              {WrappedBigNumber.from(record?.volume24hUsd || 0).formatNumberWithTooltip({
                isShowTBMK: true,
                prefix: '$',
                isShowApproximatelyEqualTo: false,
              })}
            </EmptyDataWrap>
          );
        },
      },
      {
        title: `${t('common.fairP')}/${t('common.24HCShort')}`,
        dataIndex: 'price',
        align: 'left',
        width: 100,
        // sorter: (a: IMarketPair, b: IMarketPair): number => {
        //   if (a?.fairPriceChange24h && b?.fairPriceChange24h) {
        //     return bigNumberSort(WrappedBigNumber.from(a?.fairPriceChange24h), WrappedBigNumber.from(b?.fairPriceChange24h));
        //   }
        //   return 0;
        // },
        shouldCellUpdate: (record: IMarketPair, prevRecord: IMarketPair): boolean => true,
        render: (_, record): JSX.Element | undefined => {
          return (
            <EmptyDataWrap isLoading={!record.fairPrice}>
              <div className="mobile-price-and-change">
                {WrappedBigNumber.from(record.fairPrice || 0).formatPriceNumberWithTooltip()}
                <Change24
                  instrumentAddr={record?.instrumentAddr}
                  expiry={record?.expiry}
                  chainId={record?.chainId}
                  priceChange24h={WrappedBigNumber.from(record?.fairPriceChange24h || 0)}
                />
              </div>
            </EmptyDataWrap>
          );
        },
      },
    ];
    return columns;
  }, [t]);

  const searchEmptyLine = useMemo(() => {
    return search ? t('common.market.noSearchResult') : undefined;
  }, [search, t]);

  //  only show pairs in current chain
  const filteredPairsByChainId = useMemo(
    () => filteredPairs?.filter((p) => p.chainId === chainId),
    [filteredPairs, chainId],
  );
  const sortedPairs = useMemo(
    () =>
      chainConfig?.trendingPairs?.length && !_.get(sorter, ['column'])
        ? _.orderBy(
            filteredPairsByChainId,
            [
              (p) => {
                const index = chainConfig.trendingPairs?.indexOf(p.symbol);
                return index === undefined || index < 0 ? filteredPairsByChainId?.length : index;
              },
            ],
            ['asc'],
          )
        : filteredPairsByChainId,
    [chainConfig?.trendingPairs, filteredPairsByChainId, sorter],
  );
  useWatchMarketListChange(chainId);
  const { isMockSkeleton } = useMockDevTool();

  const setTableParams = useCallback(() => {
    if (containerRef.current) {
      const scrollY =
        containerRef.current.offsetHeight - (headerRef.current ? headerRef.current.offsetHeight + 40 + 40 : 81);
      // setScrollY(scrollY > 400 ? 251 : scrollY);
      setScrollY(scrollY);
      // setScrollX(containerRef.current.offsetWidth - 16);
    }
  }, [setScrollY]);

  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (isFetched) {
      timeOut = setTimeout(() => setTableParams(), 0);
      window.onresize = setTableParams;
    }
    return () => timeOut && clearTimeout(timeOut);
    //  must depend on historyType
  }, [isFetched]);

  return (
    <Drawer
      placement="bottom"
      height="calc(100vh - 56px)"
      className="syn-trade-select-pair-drawer"
      onClose={() => onClose()}
      closable={true}
      open={showPopup}>
      <div className="syn-trade-select-pair-drawer-content" ref={containerRef}>
        <div className="syn-trade-select-pair-drawer-header" ref={headerRef}>
          <PairSearch />
          <TableSearchBar />

          {marginSelector === CUSTOM_SELECTOR.CUSTOM && marginCustomSelectorId && (
            <TradeYourOwnRiskAlert
              style={{ marginBottom: 4 }}
              i18nId={`common.tradeYourOwnRiskAlert.${marginCustomSelectorId}.list`}
            />
          )}
        </div>

        <div className="syn-trade-select-pair-drawer-list syn-scrollbar">
          <TableCard
            className="syn-trading-pairs-table"
            cardTitle=""
            sticky={{ offsetHeader: 0 }}
            loading={isMockSkeleton || (!isFetched && !marketPairList?.length)}
            loadingItem={isMockSkeleton || (!isFetched && !marketPairList?.length)}
            total={filteredPairsByChainId?.length}
            virtual={true}
            rowCount={10}
            rowClassName={classNames('market_table_card-row')}
            tableChange={(_e, _f, s) => {
              return setSorter(s);
            }}
            emptyDesc={searchEmptyLine}
            emptyIcon={search ? <NoSearchResult width={128} height={128} /> : undefined}
            emptyType="vertical"
            showEmptyTitle={!search}
            onRow={(record: IMarketPair) => ({
              onClick: (e) => {
                if ((e.target as HTMLElement).tagName === 'BUTTON') return;

                pairPageNavigate(record.symbol || '', PAIR_PAGE_TYPE.TRADE, record.chainId);
                onClose();
              },
            })}
            rowKey={(record: IMarketPair) => `${record?.symbol}-${marketColumnRowKey(record)}`}
            columns={columnDefs}
            dataSource={sortedPairs}
            scroll={sortedPairs?.length ? { y: scrollY } : undefined}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default TradingPairsDrawer;
